// bosSeed.v1.js

export const bosSeed = {
    version: "1.0.0",

    // =========================
    // 🧠 UNIVERSAL HUMAN OS (CORE LAYER)
    // =========================
    universalCore: {
        title: "🔥 Universal Human OS",
        description: "Base behavioral layer for every human regardless of role",

        layers: {
            survival: [
                "Write today’s 3 priorities (Build / Focus / Life)",
                "Mark yesterday completion (Done / Not Done / Cancelled reason)",
                "25–60 min deep focus block (no distraction)",
                "Physical activity (walk/exercise/movement)",
                "Sleep discipline check",
            ],

            growth: [
                "One skill improvement action (learn / practice / read)",
                "One financial action (save / earn / track / plan)",
                "Communication check (family / team / customer)",
            ],

            stability: [
                "Declutter one thing (digital / physical / mental)",
                "Reflect: what wasted my time today?",
                "Rate your day (1–10 + reason)",
            ],
        },
    },

    // =========================
    // 🧬 PERSONA REGISTRY (NO DUPLICATES, TAG-BASED)
    // =========================
    personas: [
        {
            id: "child",
            title: "👶 Children (School Age)",
            tags: ["education", "minor", "guided"],

            layers: {
                survival: [
                    "Homework completion",
                    "Reading 10–20 pages",
                    "No-screen control",
                    "Basic discipline task",
                ],
                growth: [
                    "Write 5 new words/concepts",
                    "Creative activity (draw/write/craft)",
                    "Outdoor play (30+ min)",
                ],
                stability: [
                    "Talk to parent about day",
                    "Sleep routine check",
                    "Help at home responsibility",
                ],
            },
        },

        {
            id: "youth",
            title: "🧑‍🎓 Gen Z / Gen Alpha (Digital Native)",
            tags: ["student", "early_career", "digital"],

            layers: {
                survival: [
                    "Study/work block completion",
                    "Physical activity",
                    "Sleep discipline check",
                ],
                growth: [
                    "1 learning module (skill/course)",
                    "30–60 min skill building",
                    "Create 1 output (post/project)",
                ],
                stability: [
                    "Social media usage tracking",
                    "Journaling emotional state",
                    "Money awareness tracking",
                    "Career exposure activity",
                ],
            },
        },

        {
            id: "professional",
            title: "💼 Professionals",
            tags: ["corporate", "service", "knowledge_worker"],

            layers: {
                survival: [
                    "Top 3 work deliverables",
                    "90-min deep work block",
                    "Email/communication cleanup",
                ],
                growth: [
                    "Skill upgrade (30 min)",
                    "Meeting efficiency improvement",
                    "Task backlog update",
                    "Networking touchpoint",
                ],
                stability: [
                    "Work quality reflection",
                    "Health movement break",
                    "Tomorrow planning",
                ],
            },
        },

        {
            id: "doctor",
            title: "👨‍⚕️ Doctor",
            tags: ["healthcare", "medical"],

            layers: {
                survival: [
                    "Patient rounds / consultation review",
                    "Patient follow-up tracking",
                    "Emergency readiness check",
                ],
                growth: [
                    "Case study learning",
                    "Medical journal update",
                    "Skill/procedure learning",
                    "Peer case discussion",
                ],
                stability: [
                    "Documentation accuracy",
                    "Health compliance review",
                    "Stress recovery break",
                ],
            },
        },

        {
            id: "engineer",
            title: "👷 Engineer",
            tags: ["tech", "construction", "systems"],

            layers: {
                survival: [
                    "Design/build task execution",
                    "Bug/problem debugging",
                    "Project progress update",
                ],
                growth: [
                    "Skill learning (tools/tech)",
                    "Code/system improvement",
                    "Team coordination",
                    "Innovation exploration",
                ],
                stability: [
                    "Technical documentation",
                    "Quality testing",
                    "Work reflection log",
                ],
            },
        },

        {
            id: "msme",
            title: "🏭 MSME Ecosystem",
            tags: ["business", "entrepreneur", "micro"],

            layers: {
                survival: [
                    "Stock/inventory check",
                    "Sales tracking",
                    "Daily income/expense log",
                    "Customer interaction",
                    "Operations execution",
                ],
                growth: [
                    "Supplier coordination",
                    "Demand observation",
                    "Pricing/strategy adjustment",
                    "Marketing activity",
                ],
                stability: [
                    "Cash discipline",
                    "Waste reduction",
                    "Savings/business planning",
                ],
            },
        },

        {
            id: "govt",
            title: "🏛️ Government Ecosystem",
            tags: ["public_sector", "policy"],

            layers: {
                survival: [
                    "File/work processing",
                    "Citizen service handling",
                    "Reporting tasks",
                    "Field/office execution",
                ],
                growth: [
                    "Policy / compliance work",
                    "Audit / review",
                    "Decision logging",
                    "Coordination",
                ],
                stability: [
                    "Governance discipline",
                    "Record maintenance",
                    "System cleanup",
                ],
            },
        },

        {
            id: "family",
            title: "👨‍👩‍👧‍👦 Family / Household",
            tags: ["home", "social_core"],

            layers: {
                survival: [
                    "Household planning",
                    "Budget tracking",
                    "Chores distribution",
                    "Problem resolution",
                ],
                growth: [
                    "Savings & future planning",
                    "Children education tracking",
                    "Family communication improvement",
                ],
                stability: [
                    "Health check routine",
                    "Emotional check-in",
                ],
            },
        },

        {
            id: "wellness",
            title: "🧠 Mental Health / Wellness",
            tags: ["health", "emotional"],

            layers: {
                survival: [
                    "Mood tracking",
                    "Sleep tracking",
                    "Stress level logging",
                    "Movement activity",
                ],
                growth: [
                    "Trigger identification",
                    "Recovery habits",
                    "Self-reflection journaling",
                ],
                stability: [
                    "Digital detox tracking",
                    "Emotional regulation practice",
                ],
            },
        },

        {
            id: "recovery",
            title: "🧠 Discipline Recovery Users",
            tags: ["behavior_change", "addiction_recovery"],

            layers: {
                survival: [
                    "Relapse tracking",
                    "Clean streak tracking",
                    "Trigger awareness logging",
                ],
                growth: [
                    "Replacement habits",
                    "Support engagement",
                    "Daily wins tracking",
                ],
                stability: [
                    "Emotional logging",
                    "Reset recovery tasks",
                    "Identity reinforcement",
                ],
            },
        },
    ],

    // =========================
    // 🧠 BEHAVIOR ENGINE (CORE INTELLIGENCE LAYER)
    // =========================
    behaviorEngine: {
        profiles: {
            focus: {
                description: "Deep work + distraction control",
                signals: ["deep_work", "priority_execution", "focus_time"],
            },

            execution: {
                description: "Task completion + output momentum",
                signals: ["task_completion", "speed", "output_rate"],
            },

            recovery: {
                description: "Burnout + rest + reset system",
                signals: ["rest", "burnout", "pause_behavior"],
            },

            growth: {
                description: "Learning + skill progression",
                signals: ["learning", "skill_building", "improvement"],
            },

            discipline: {
                description: "Consistency + habit correction",
                signals: ["consistency", "habit_correction", "streak"],
            },
        },

        // THIS IS WHERE AI PERSONALIZATION LIVES
        scoring: {
            focusWeight: 0.25,
            executionWeight: 0.25,
            recoveryWeight: 0.15,
            growthWeight: 0.20,
            disciplineWeight: 0.15,
        },
    },

    // =========================
    // 🧩 SYSTEM RULES (VERY IMPORTANT)
    // =========================
    systemRules: {
        principles: [
            "One user can have multiple personas",
            "Universal Core always applies",
            "Personas modify behavior, not replace core",
            "Tasks must map to Survival / Growth / Stability",
            "No duplicate persona definitions",
            "System must remain UI-renderable (accordion-friendly)",
        ],

        uiDesignRule: {
            defaultView: "universalCore + activePersona",
            expandMode: "accordion by layers",
            avoidOverload: true,
        },
    },
};